import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNetwork } from '../../../../../../../utils/network';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SubscriptionCarousel from '../SubscriptionCarousel/SubscriptionCarousel';
import CloseIcon from '@mui/icons-material/Close';
import RaffleStripeCheckoutForm from '../RafflePaymentPage/RaffleStripeCheckoutForm';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../../../../../../../utils/stripe';
import { useShowSnackbar } from '../../../../../../../utils/snackbar';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  getFirstMonthDiscountedPrice,
  isDiscountEligibleSubscriptionPackage
} from '../../../../../../../utils/subscriptionDiscounts';

type ApiPackage = {
  id: number;
  name: string;
  type: 'Standalone' | 'Subscription';
  description: string | null;
  imageUrl?: string | null;
  price: string | number;
  cards?: number | null;
  tokens?: number | null;
  entries?: number | null;
};

export type PackItem = {
  id: string;
  title: string;
  price: string;
  entries: number;
  nfts: number;
  tokens?: number;
  description: string;
  dotColor: string;
};

type CheckoutMode = 'standalone' | 'subscription';

// Stripe.js instance is the shared toggle-aware one imported above (utils/stripe).

type Props = {};

const EmailPromo = (props: Props) => {
  const { get, post } = useNetwork();

  const { showSnackbar } = useShowSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const formSectionRef = useRef<HTMLDivElement | null>(null);

  const voucherCode =
    new URLSearchParams(location.search).get('discount') || '';

  const [allSubscriptions, setAllSubscriptions] = useState<ApiPackage[]>([]);
  const [chosenSubId, setChosenSubId] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState<boolean>(false);
  const [checkoutMode, setCheckoutMode] =
    useState<CheckoutMode>('subscription');
  const [email, setEmail] = useState<string>('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [markEmailTouched, setMarkEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailAttempted, setEmailAttempted] = useState(false);
  const [emailStatus, setEmailStatus] = useState<string>('idle');
  const [accountVerified, setAccountVerified] = useState<boolean | null>();
  const [raffleUnlockToken, setRaffleUnlockToken] = useState<string | null>(
    null
  );
  const [discountPercantage, setDiscountPercantage] = useState<number>(30);
  const [promoLinkStatus, setPromoLinkStatus] = useState<
    'checking' | 'valid' | 'invalid'
  >('checking');
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [activeSubscriptionPackage, setActiveSubscriptionPackage] = useState<{
    id: number;
    name: string;
    price: string | number;
  } | null>(null);

  useEffect(() => {
    const getSubscriptionPackages = async () => {
      try {
        const result = await get('/package/subscription', true);
        setAllSubscriptions(result);
      } catch (error: any) {
        console.error('Error fetching subscription packages:', error);
      }
    };

    getSubscriptionPackages();
  }, []);

  useEffect(() => {
    const validatePromoCode = async () => {
      setPromoLinkStatus('checking');

      try {
        if (!voucherCode) {
          setPromoLinkStatus('invalid');
          return;
        }

        const res = await post('/raffle-auth/validate-email-promo-token', {
          voucherCode
        });

        if (!res?.valid) {
          setPromoLinkStatus('invalid');
          return;
        }

        setDiscountPercantage(Number(res?.discountPercentage) === 50 ? 50 : 30);
        setPromoLinkStatus('valid');
      } catch (error) {
        setPromoLinkStatus('invalid');
      }
    };

    validatePromoCode();
  }, [voucherCode]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const standaloneDotColours = [
    'linear-gradient(180deg,#B14CFF,#7B1BFF)', // Purple
    'linear-gradient(180deg,#7CFF3A,#3AAE12)', // Green
    'linear-gradient(180deg,#FF4A4A,#B30000)', // Red
    'linear-gradient(180deg,#FFD700,#C79A00)' // Gold
  ];

  const toPackSubscriptionItems = (pkgs: ApiPackage[]): PackItem[] =>
    pkgs.map((p, idx) => ({
      id: String(p.id),
      title: p.name,
      price: typeof p.price === 'number' ? `${p.price}` : String(p.price),
      entries: Number(p.entries || 0),
      nfts: Number(p.cards || 0),
      tokens: Number(p.tokens || 0),
      description: p.description || '',
      dotColor: standaloneDotColours[idx % standaloneDotColours.length]
    }));

  const subscriptionItems = useMemo(
    () => toPackSubscriptionItems(allSubscriptions),
    [allSubscriptions]
  );

  const selectedSubscriptionPack = useMemo(() => {
    if (!chosenSubId) return null;
    return subscriptionItems.find((p) => p.id === chosenSubId) ?? null;
  }, [chosenSubId, subscriptionItems]);

  const closeCheckout = () => {
    setCheckoutOpen(false);
  };

  const openCheckout = (mode: CheckoutMode) => {
    if (mode === 'subscription' && !selectedSubscriptionPack) return;
    setCheckoutMode(mode);
    setCheckoutOpen(true);
  };

  const packForCheckout = useMemo(() => {
    if (checkoutMode === 'subscription') return selectedSubscriptionPack;
  }, [checkoutMode, selectedSubscriptionPack]);

  const getDisplayedSubscriptionPrice = (
    pack: PackItem,
    discountPercentage: number
  ) => {
    const basePrice = Number(pack.price ?? 0);

    const isDiscountEligibleSubscription =
      checkoutMode === 'subscription' &&
      isDiscountEligibleSubscriptionPackage(pack.id);

    if (isDiscountEligibleSubscription) {
      return getFirstMonthDiscountedPrice(basePrice, discountPercentage);
    }

    return basePrice;
  };

  const amountCents = Math.round(
    (packForCheckout
      ? getDisplayedSubscriptionPrice(packForCheckout, discountPercantage)
      : 0) * 100
  );

  const elementsKey = `${checkoutMode}:${amountCents}:${'off_session'}`;

  const getSubscriptionStartingBonusEntries = (pack: PackItem) =>
    Math.max(0, Math.floor(Number(pack.entries || 0) * 10));

  const computeTotals = (pack: PackItem) => {
    const baseEntries = Number(pack.entries ?? 0);
    const baseNfts = Number(pack.nfts ?? 0);
    const starterBonusEntries =
      checkoutMode === 'subscription'
        ? getSubscriptionStartingBonusEntries(pack)
        : 0;

    const basePrice = getDisplayedSubscriptionPrice(pack, discountPercantage);

    return {
      totalEntries: baseEntries + starterBonusEntries,
      nftRewards: baseNfts,
      amountPaidAud: basePrice
    };
  };

  const saveConfirmationDraft = (opts?: { mode?: 'subscription' }) => {
    const pack = packForCheckout!;
    const mode = opts?.mode ?? checkoutMode;

    const totals = computeTotals(pack);

    const confirmationState = {
      entry: { email },
      selectedPack: pack,

      // ✅ For subscription, these are “per month” values (based on pack)
      totalEntries: totals.totalEntries,
      nftRewards: totals.nftRewards,
      amountPaidAud: totals.amountPaidAud,

      // ✅ NEW
      checkoutMode: mode,
      incPerMonth:
        mode === 'subscription' ? Number(pack.tokens ?? 0) : undefined,

      memberPortalPath: '/login'
    };

    sessionStorage.setItem(
      'giveawayConfirmationState',
      JSON.stringify(confirmationState)
    );
  };

  const goToConfirmation = () => {
    navigate('/giveaway/confirmation-sub', { replace: true });
  };

  const isEmailValid = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  const handlePaid = async (args: {
    mode: CheckoutMode;
    paymentIntentId?: string;
    bonusSelected?: boolean;
  }) => {
    saveConfirmationDraft({ mode: 'subscription' });
    closeCheckout();
    goToConfirmation();
  };

  const checkEmail = async (email: string) => {
    const res = await post('/auth/check-email', { email });

    if (typeof res === 'boolean') {
      return {
        exists: res,
        verified: false,
        hasActiveSubscription: false,
        activeSubscriptionPackage: null
      };
    }

    return {
      exists: !!res?.exists,
      verified: !!res?.verified,
      hasActiveSubscription: !!res?.hasActiveSubscription,
      activeSubscriptionPackage: res?.activeSubscriptionPackage || null
    };
  };

  const handleEmailContinue = async () => {
    if (promoLinkStatus !== 'valid') {
      setEmailError('This promo link is invalid or has expired.');
      return;
    }

    const emailToLowerCase = email.trim().toLowerCase();

    setEmailAttempted(true);
    setMarkEmailTouched(true);

    if (!emailToLowerCase) {
      setEmailError('Email is required.');
      return;
    }

    if (!isEmailValid(emailToLowerCase)) {
      setEmailError('Please enter a valid email.');
      return;
    }

    setEmailError('');
    setEmailStatus('checking');

    try {
      const {
        exists,
        verified,
        hasActiveSubscription,
        activeSubscriptionPackage
      } = await checkEmail(emailToLowerCase);

      setEmailStatus(exists ? 'exists' : 'new');
      setEmailChecked(true);
      setAccountVerified(verified);
      setHasActiveSubscription(hasActiveSubscription);
      setActiveSubscriptionPackage(activeSubscriptionPackage);

      if (exists && verified) {
        const authRes = await post('/raffle-auth/email-promo-auth', {
          email: emailToLowerCase,
          voucherCode
        });

        const token = String(authRes?.raffleUnlockToken || '');
        if (!token) {
          throw new Error('Missing raffle unlock token');
        }

        setRaffleUnlockToken(token);

        if (Number(authRes?.discountPercentage) === 50) {
          setDiscountPercantage(50);
        } else {
          setDiscountPercantage(30);
        }
      }
    } catch (error: any) {
      showSnackbar(error?.message || 'Error checking email', 'error');
      setEmailStatus('idle');
      setEmailChecked(false);
      setAccountVerified(null);
      setRaffleUnlockToken(null);
      setEmailError(error?.message || 'Error checking email');
    }
  };

  const resetEmailGate = () => {
    setEmailStatus('idle');
    setEmailChecked(false);
    setEmailAttempted(false);
    setMarkEmailTouched(false);
    setAccountVerified(null);
    setRaffleUnlockToken(null);
    setEmailError('');
    setHasActiveSubscription(false);
    setActiveSubscriptionPackage(null);
  };

  const showExistingNeedsEmailVerify =
    emailChecked && emailStatus === 'exists' && accountVerified === false;

  const checkoutPayload = useMemo(() => {
    if (!packForCheckout || !email) return undefined;

    const signupCodeBonusEntries =
      checkoutMode === 'subscription'
        ? getSubscriptionStartingBonusEntries(packForCheckout)
        : 0;

    return {
      entry: {
        firstName: '',
        lastName: '',
        username: '',
        email: email.trim().toLowerCase(),
        phone: '',
        countryIso2: '',
        countryName: '',
        stateIso2: '',
        stateName: '',
        phoneCountryCode: '',
        agreeToTerms: true,
        eligible: 'yes' as const
      },
      selectedPack: packForCheckout,
      standaloneItems: [],
      subscriptionItems,
      raffleUnlockToken,
      signupCodeBonusEntries:
        signupCodeBonusEntries > 0 ? signupCodeBonusEntries : undefined,
      signupCodeBonusSource:
        signupCodeBonusEntries > 0 ? ('signup_code' as const) : undefined
    };
  }, [
    checkoutMode,
    packForCheckout,
    email,
    subscriptionItems,
    raffleUnlockToken
  ]);

  const visibleSubscriptionItems = useMemo(() => {
    if (!hasActiveSubscription || !activeSubscriptionPackage) {
      return subscriptionItems;
    }

    const currentPrice = Number(activeSubscriptionPackage.price || 0);

    return subscriptionItems.filter(
      (pack) => Number(pack.price || 0) > currentPrice
    );
  }, [subscriptionItems, hasActiveSubscription, activeSubscriptionPackage]);

  const topPack = useMemo(() => {
    if (!subscriptionItems.length) return null;

    return [...subscriptionItems].sort(
      (a, b) => Number(b.price || 0) - Number(a.price || 0)
    )[0];
  }, [subscriptionItems]);

  const hasTopPackAlready = useMemo(() => {
    if (!hasActiveSubscription || !activeSubscriptionPackage || !topPack) {
      return false;
    }

    return Number(activeSubscriptionPackage.id) === Number(topPack.id);
  }, [hasActiveSubscription, activeSubscriptionPackage, topPack]);

  const scrollToFormSection = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  };

  const handleSubscriptionSelect = (id: string) => {
    setChosenSubId(id);
    scrollToFormSection();
  };

  if (promoLinkStatus !== 'valid') {
    if (promoLinkStatus === 'invalid') {
      return <Navigate to="/" replace />;
    }

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8fafc',
          px: 2
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 560,
            borderRadius: 4,
            border: '1px solid rgba(15,23,42,0.10)',
            background: '#ffffff',
            boxShadow: '0 20px 60px rgba(15,23,42,0.08)',
            p: { xs: 3, sm: 4 },
            textAlign: 'center'
          }}
        >
          <CircularProgress size={32} sx={{ color: '#0f172a', mb: 2 }} />
          <Typography sx={{ fontWeight: 900, color: '#0f172a' }}>
            Checking promo link...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'white', width: '100%' }}>
      <Box
        sx={{
          py: { xs: 5, md: 7 },
          px: 2,
          textAlign: 'center',
          backgroundColor: 'white'
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 0.8,
            mb: 2.5,
            borderRadius: 999,
            backgroundColor:
              discountPercantage === 50
                ? 'rgba(255, 215, 0, 0.12)'
                : 'rgba(0,0,0,0.05)',
            border:
              discountPercantage === 50
                ? '1px solid rgba(255, 215, 0, 0.35)'
                : '1px solid rgba(0,0,0,0.08)'
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 900,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'black'
            }}
          >
            {discountPercantage === 50 ? 'Exclusive Offer' : 'Special Offer'}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: { xs: 34, md: 54 },
            fontWeight: 900,
            color: 'black',
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            mb: 2
          }}
        >
          Your Exclusive Subscription Offer
        </Typography>

        <Typography
          sx={{
            maxWidth: 820,
            mx: 'auto',
            fontSize: { xs: 16, md: 20 },
            fontWeight: 500,
            lineHeight: 1.6,
            color: 'rgba(0,0,0,0.72)'
          }}
        >
          {discountPercantage === 50
            ? `We’re offering you 50% off your first month on eligible top-tier subscriptions.`
            : `We’re offering 30% off your first month on eligible top-tier subscriptions.`}
        </Typography>
        <Typography
          sx={{
            mt: 4,
            maxWidth: 820,
            mx: 'auto',
            fontSize: { xs: 16, md: 20 },
            fontWeight: 500,
            lineHeight: 1.6,
            color: 'rgba(0,0,0,0.72)'
          }}
        >
          All of our memberships include recurring entries and monthly rewards,
          with the higher tiers delivering the strongest value and benefits.
        </Typography>
      </Box>

      <SubscriptionCarousel
        items={visibleSubscriptionItems}
        selectedId={chosenSubId ?? ''}
        onSelect={handleSubscriptionSelect}
        lastPackDiscountPercent={discountPercantage}
      />

      <Box
        ref={formSectionRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: { xs: 4, md: 5 },
          pb: { xs: 5, md: 6 },
          px: 2,
          scrollMarginTop: { xs: '16px', md: '24px' }
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 680,
            borderRadius: 4,
            border: '1px solid rgba(15,23,42,0.10)',
            background:
              'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.95) 100%)',
            boxShadow: '0 20px 60px rgba(15,23,42,0.08)',
            p: { xs: 2.25, sm: 3, md: 4 }
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              sx={{
                fontSize: { xs: 24, md: 28 },
                fontWeight: 950,
                color: '#0f172a',
                letterSpacing: '-0.02em'
              }}
            >
              Check Your Eligibility
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: 15,
                fontWeight: 600,
                color: 'rgba(15,23,42,0.68)',
                lineHeight: 1.5,
                maxWidth: 520,
                mx: 'auto'
              }}
            >
              Enter your email to continue with your exclusive subscription
              offer.
            </Typography>
          </Box>

          {/* EMAIL STEP */}
          <Box sx={{ width: '100%' }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 900,
                mb: 0.8,
                color: '#0f172a'
              }}
            >
              Email Address
            </Typography>

            <TextField
              fullWidth
              placeholder="john@example.com"
              value={email}
              disabled={emailChecked}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailChecked) {
                  setEmailChecked(false);
                  setEmailStatus('idle');
                  setAccountVerified(null);
                  setRaffleUnlockToken(null);
                  setHasActiveSubscription(false);
                  setActiveSubscriptionPackage(null);
                }
              }}
              onBlur={() => setMarkEmailTouched(true)}
              error={(markEmailTouched || emailAttempted) && !!emailError}
              helperText={
                markEmailTouched || emailAttempted ? emailError || ' ' : ' '
              }
              FormHelperTextProps={{
                sx: {
                  color: emailError ? '#dc2626' : 'rgba(15,23,42,0.55)',
                  fontWeight: 700,
                  ml: 0,
                  mt: 0.8
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999,
                  background: emailChecked ? '#f1f5f9' : '#fff',
                  border: '1px solid rgba(15,23,42,0.10)',
                  transition: 'all 0.2s ease',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    borderColor: emailChecked
                      ? 'rgba(15,23,42,0.10)'
                      : 'rgba(15,23,42,0.22)'
                  },
                  '&.Mui-focused': {
                    borderColor: '#111827',
                    boxShadow: '0 0 0 4px rgba(17,24,39,0.06)'
                  }
                },

                '& .MuiInputBase-input': {
                  padding: '15px 18px',
                  fontSize: 16,
                  fontWeight: 700,
                  color: emailChecked ? 'rgba(15,23,42,0.75)' : '#0f172a',
                  WebkitTextFillColor: emailChecked
                    ? 'rgba(15,23,42,0.75)'
                    : '#0f172a'
                },

                // ✅ FORCE placeholder to be lighter (this overrides Webkit)
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(15,23,42,0.35)',
                  WebkitTextFillColor: 'rgba(15,23,42,0.35)', // 👈 KEY FIX
                  opacity: 1,
                  fontWeight: 500 // 👈 optional but helps visually
                },

                // Disabled state placeholder (extra safety)
                '& .MuiInputBase-input.Mui-disabled::placeholder': {
                  color: 'rgba(15,23,42,0.30)',
                  WebkitTextFillColor: 'rgba(15,23,42,0.30)'
                }
              }}
            />
          </Box>

          {!emailChecked && (
            <Button
              fullWidth
              onClick={handleEmailContinue}
              disabled={
                !email.trim() ||
                !isEmailValid(email) ||
                emailStatus === 'checking'
              }
              sx={{
                mt: 1.2,
                height: 52,
                fontSize: 15,
                fontWeight: 950,
                borderRadius: 999,
                color: 'white',
                background: 'linear-gradient(90deg, #16a34a 0%, #15803d 100%)',
                boxShadow: '0 14px 30px rgba(21,128,61,0.22)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #15803d 0%, #166534 100%)'
                },
                '&.Mui-disabled': {
                  color: 'rgba(255,255,255,0.7)',
                  background: 'rgba(15,23,42,0.18)',
                  boxShadow: 'none'
                }
              }}
            >
              {emailStatus === 'checking' ? (
                <>
                  <CircularProgress
                    size={18}
                    sx={{ mr: 1.2, color: 'white' }}
                  />
                  Checking…
                </>
              ) : (
                'Continue'
              )}
            </Button>
          )}

          {emailChecked && (
            <Box sx={{ mt: 1 }}>
              {emailStatus === 'new' && (
                <Box
                  sx={{
                    mt: 2,
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: '1px solid rgba(239,68,68,0.18)',
                    background: 'rgba(254,242,242,0.95)',
                    textAlign: 'center'
                  }}
                >
                  <Button
                    variant="text"
                    onClick={resetEmailGate}
                    sx={{
                      mb: 1.5,
                      fontWeight: 900,
                      color: '#0f172a',
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                      '&:hover': {
                        opacity: 0.85,
                        background: 'transparent'
                      }
                    }}
                  >
                    Change email
                  </Button>

                  <Typography
                    sx={{
                      color: '#b91c1c',
                      fontSize: 20,
                      fontWeight: 950
                    }}
                  >
                    Email does not exist
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1,
                      color: 'rgba(15,23,42,0.68)',
                      fontWeight: 700,
                      lineHeight: 1.5
                    }}
                  >
                    This offer is for existing members only. Create an account
                    to view our available packs and subscription options.
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={() => navigate(`/giveaway`)}
                    sx={{
                      mt: 2.5,
                      height: 54,
                      px: 4,
                      borderRadius: 999,
                      fontWeight: 950,
                      color: 'white',
                      background:
                        'linear-gradient(90deg, #16a34a 0%, #15803d 100%)',
                      '&:hover': {
                        background:
                          'linear-gradient(90deg, #15803d 0%, #166534 100%)'
                      }
                    }}
                  >
                    See What We Offer
                  </Button>
                </Box>
              )}

              {hasTopPackAlready ? (
                <Box
                  sx={{
                    mt: 2,
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    border: '1px solid rgba(34,197,94,0.18)',
                    background: 'rgba(240,253,244,0.95)',
                    textAlign: 'center'
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 950,
                      fontSize: 22,
                      color: '#166534'
                    }}
                  >
                    You already have our highest-tier subscription
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1,
                      color: 'rgba(15,23,42,0.72)',
                      fontWeight: 700,
                      lineHeight: 1.5
                    }}
                  >
                    Thank you for being a valued part of the INCRYPT community.
                    You’re already on our best membership tier.
                  </Typography>
                </Box>
              ) : (
                <>
                  {emailStatus === 'exists' &&
                    !showExistingNeedsEmailVerify && (
                      <Box
                        sx={{
                          mt: 2,
                          p: { xs: 2, sm: 2.25 },
                          borderRadius: 3,
                          border: '1px solid rgba(15,23,42,0.08)',
                          background: 'rgba(248,250,252,0.85)',
                          textAlign: 'center'
                        }}
                      >
                        <Button
                          variant="text"
                          onClick={resetEmailGate}
                          sx={{
                            mb: 1.5,
                            fontWeight: 900,
                            color: '#0f172a',
                            textDecoration: 'underline',
                            textUnderlineOffset: '3px',
                            '&:hover': {
                              opacity: 0.85,
                              background: 'transparent'
                            }
                          }}
                        >
                          Change email
                        </Button>

                        <Typography
                          sx={{
                            fontSize: 20,
                            fontWeight: 950,
                            color: '#0f172a'
                          }}
                        >
                          Your offer is ready
                        </Typography>

                        <Typography
                          sx={{
                            mt: 1,
                            color: 'rgba(15,23,42,0.68)',
                            fontWeight: 700,
                            lineHeight: 1.5
                          }}
                        >
                          Continue to checkout to claim your subscription offer.
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: 2.5
                          }}
                        >
                          <Button
                            onClick={() => openCheckout('subscription')}
                            disabled={
                              !email || !chosenSubId || !raffleUnlockToken
                            }
                            sx={{
                              minWidth: 240,
                              height: 54,
                              px: 4,
                              borderRadius: 999,
                              fontWeight: 950,
                              color: 'white',
                              background:
                                'linear-gradient(90deg, #16a34a 0%, #15803d 100%)',
                              boxShadow: '0 14px 30px rgba(21,128,61,0.20)',
                              '&.Mui-disabled': {
                                color: 'rgba(255,255,255,0.7)',
                                background: 'rgba(15,23,42,0.18)',
                                boxShadow: 'none'
                              },
                              '&:hover': {
                                background:
                                  'linear-gradient(90deg, #15803d 0%, #166534 100%)'
                              }
                            }}
                          >
                            Purchase Subscription
                          </Button>
                        </Box>
                      </Box>
                    )}
                </>
              )}
            </Box>
          )}

          {/* EXISTS BUT NOT VERIFIED */}
          {showExistingNeedsEmailVerify && emailStatus === 'exists' && (
            <Box
              sx={{
                mt: 2.4,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                border: '1px solid rgba(245,158,11,0.20)',
                background: 'rgba(255,251,235,0.95)'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.2,
                  textAlign: 'center'
                }}
              >
                <MarkEmailUnreadOutlinedIcon sx={{ color: '#d97706' }} />
                <Typography
                  sx={{
                    fontWeight: 950,
                    fontSize: 18,
                    color: '#92400e'
                  }}
                >
                  Please verify your email first
                </Typography>
              </Box>

              <Typography
                sx={{
                  mt: 1.5,
                  fontWeight: 700,
                  color: 'rgba(15,23,42,0.72)',
                  lineHeight: 1.5,
                  textAlign: 'center'
                }}
              >
                We’ve sent a verification link to <b>{email.trim()}</b>. Open it
                to activate your account, then come back here to continue.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={checkoutOpen}
        fullScreen={isMobile}
        maxWidth={false}
        disableEscapeKeyDown={true}
        onClose={closeCheckout}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '50vw',
            maxWidth: isMobile ? '100%' : 760,
            borderRadius: isMobile ? 0 : 4,
            background: 'rgba(10, 14, 16, 0.92)',
            border: '1px solid rgba(124,255,250,0.18)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <Typography
            sx={{
              fontWeight: 950,
              color: 'rgba(240,255,255,0.92)',
              fontSize: 16
            }}
          >
            Checkout
          </Typography>

          <Box sx={{ ml: 'auto' }}>
            <IconButton
              onClick={closeCheckout}
              sx={{ color: 'rgba(255,255,255,0.75)' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {packForCheckout && checkoutPayload && amountCents > 0 && (
            <Elements
              key={elementsKey}
              stripe={stripePromise}
              options={{
                mode: 'subscription',
                currency: 'aud',
                paymentMethodTypes: ['card'],
                setupFutureUsage: 'off_session',
                amount: amountCents,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    fontSizeBase: '16px'
                  },
                  rules: { '.Label': { color: 'white' } }
                }
              }}
            >
              <RaffleStripeCheckoutForm
                payload={checkoutPayload}
                mode={checkoutMode}
                pack={packForCheckout}
                onPaid={handlePaid}
                subDiscount={discountPercantage}
              />
            </Elements>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default EmailPromo;
